import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from '../components/ConfirmModal';
import ImpersonateModal from '../components/ImpersonateModal';

interface Piano {
  id: string;
  nome: string;
  slug: string;
  max_utenti: number;
  max_offerte_mese: number;
  max_storage_mb: number;
}

interface Tenant {
  id: string;
  nome: string;
  slug: string;
  email_admin: string;
  piano_id: string;
  piano: Piano;
  partita_iva?: string;
  codice_fiscale?: string;
  indirizzo?: string;
  telefono?: string;
  email?: string;
  sito_web?: string;
  logo_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Tenants = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; nome: string } | null>(null);
  const [impersonateConfirm, setImpersonateConfirm] = useState<Tenant | null>(null);

  // Check if user is host
  if (!user?.is_host) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Accesso negato. Solo gli host users possono gestire i tenant.</p>
        </div>
      </div>
    );
  }

  // Fetch tenants
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', page, search],
    queryFn: async () => {
      const response = await tenantsAPI.getTenants({
        page,
        limit: 20,
        search: search || undefined,
      });
      return response.data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantsAPI.deleteTenant(id),
    onSuccess: () => {
      toast.success('Tenant eliminato con successo');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione del tenant');
    },
  });

  // Impersonate mutation
  const impersonateMutation = useMutation({
    mutationFn: ({ tenant_id, user_id }: { tenant_id: string; user_id: string }) =>
      tenantsAPI.impersonate(tenant_id, user_id),
    onSuccess: (response) => {
      const { access_token, refresh_token, tenant: tenantData, user: userData } = response.data as {
        access_token: string;
        refresh_token: string;
        user: { name: string; email: string };
        tenant: { name: string };
      };
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      toast.success(`Impersonando ${userData.name || userData.email} in ${tenantData.name}`);
      window.location.href = '/';
    },
    onError: () => {
      toast.error('Errore durante l\'impersonificazione');
    },
  });

  const handleDelete = (id: string, nome: string) => {
    setDeleteConfirm({ id, nome });
  };

  const handleImpersonate = (tenant: Tenant) => {
    setImpersonateConfirm(tenant);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Tenant</h1>
            <p className="text-gray-600 mt-1">Gestisci i tenant della piattaforma</p>
          </div>
          <button
            onClick={() => navigate('/tenants/new')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuovo Tenant
          </button>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Cerca per nome, email o slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Tenants List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Errore nel caricamento dei tenant</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Piano
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limiti
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creato
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Nessun tenant trovato
                  </td>
                </tr>
              ) : (
                data?.data.map((tenant: Tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{tenant.nome}</div>
                        <div className="text-sm text-gray-500">{tenant.email_admin}</div>
                        <div className="text-xs text-gray-400">/{tenant.slug}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {tenant.piano.nome}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>{tenant.piano.max_utenti} utenti</div>
                      <div>{tenant.piano.max_offerte_mese} offerte/mese</div>
                      <div className="text-xs text-gray-400">{tenant.piano.max_storage_mb} MB storage</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        tenant.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {tenant.is_active ? 'Attivo' : 'Inattivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(tenant.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleImpersonate(tenant)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Impersona tenant"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/tenants/${tenant.id}/edit`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Modifica"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(tenant.id, tenant.nome)}
                          className="text-red-600 hover:text-red-900"
                          title="Elimina"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Precedente
                </button>
                <button
                  onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                  disabled={page === data.pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Successivo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Pagina <span className="font-medium">{page}</span> di{' '}
                    <span className="font-medium">{data.pagination.pages}</span>
                    {' '}({data.pagination.total} tenant totali)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                      disabled={page === data.pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (deleteConfirm) {
            deleteMutation.mutate(deleteConfirm.id);
            setDeleteConfirm(null);
          }
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il tenant "${deleteConfirm?.nome}"?`}
        confirmLabel="Elimina"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Impersonate Modal */}
      <ImpersonateModal
        isOpen={!!impersonateConfirm}
        tenant={impersonateConfirm}
        onClose={() => setImpersonateConfirm(null)}
        onConfirm={(tenantId, userId) => {
          impersonateMutation.mutate({ tenant_id: tenantId, user_id: userId });
          setImpersonateConfirm(null);
        }}
        isLoading={impersonateMutation.isPending}
      />
    </div>
  );
};

export default Tenants;
